import React, { useContext, useState, useEffect, useRef } from "react";
import { ApiLogout } from "../../../services/api";
import { AuthContext } from "../../../context/AuthContext";
import { LOGIN_ROUTE } from "../../../global/constants/routes/defaultConstants";
import { useNavigate } from "react-router-dom";
import { User, LogOut, Settings } from "../../ui/icons";
import styled from "styled-components";
import { ADMIN_ROUTE } from "../../Router";
import { Button } from "../../ui/buttons/button";
import { toast } from "sonner";
import { useErrorToast } from "../../../hooks/useErrorToast";

const StyledMenuButton = styled.button`
  height: 52px;
  padding: 16px 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  background: none;
  border: none;

  svg {
    width: 16px;
    height: 16px;
  }

  span {
    color: #858890;
    font-size: 13px;
    font-family: 'Work Sans', sans-serif;
    font-weight: 600;
    text-transform: uppercase;
    line-height: 16px;
    letter-spacing: 1.04px;
    word-wrap: break-word;
  }

  &:hover span {
    text-decoration: underline;
  }
`;

const MenuContainer = styled.div`
  position: absolute;
  right: 2rem;
  top: 5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  background: white;
  border-radius: 4px;
  box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.15);
  padding: 0.5rem;
  min-width: 120px;
`;

interface ProfileMenuProps {
  userName?: string;
}

export const ProfileMenu: React.FC<ProfileMenuProps> = ({ userName }) => {
  const { logout, isAdmin } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const errorToast = useErrorToast();
  const navigate = useNavigate();
  const menuRef = useRef<HTMLDivElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      const { message } = await ApiLogout();
      toast.success(message);
      logout();
      navigate(LOGIN_ROUTE);
    } catch (error) {
      errorToast((error as Error).message);
    }
    handleClose();
  };

  const handleConfig = () => {
    navigate(ADMIN_ROUTE);
    handleClose();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        anchorEl &&
        !anchorEl.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open, anchorEl]);

  return (
    <>
      <StyledMenuButton onClick={handleClick}>
        <User size={16} />
        <span>{userName || "PERFIL"}</span>
      </StyledMenuButton>
      {open && (
        <MenuContainer ref={menuRef}>
          {isAdmin && (
            <Button
              variant="ghost"
              onClick={handleConfig}
              className="w-full justify-start text-black font-medium"
            >
              <Settings size={20} className="mr-2" />
              Configuração
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-black font-medium"
          >
            <LogOut size={20} className="mr-2" />
            Sair
          </Button>
        </MenuContainer>
      )}
    </>
  );
};
